/**
 * Scene-editor chrome for playable size + default version.
 * Same APIs / behavior as TreeView cards; dark inspector look only.
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@material-ui/data-grid";
import _ from "lodash";
import { getServerUri } from "../../modules/uriUtility";
import { calculateVersionPlayableSize } from "../../modules/playableSizeCalculator";
import { BAR_BG, FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "./sceneEditorInspector";

const INPUT_BG = "#212226";
const TEXT = "#e8e8e8";
const MUTED = "#8a8d93";

const useDialogStyles = makeStyles({
  paper: {
    height: "40rem",
    backgroundColor: PANEL_BG,
    color: TEXT,
    border: `1px solid ${SECTION_DIVIDER}`,
  },
});

function MetaBar({ title, children, action }) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          padding: "8px 12px",
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{title}</span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px 10px",
          backgroundColor: PANEL_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            color: TEXT,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {children}
        </div>
        {action ? <div style={{ flexShrink: 0 }}>{action}</div> : null}
      </div>
    </div>
  );
}

function MetaValue({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "96px minmax(0, 1fr)",
        columnGap: 8,
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: FIELD_LABEL,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

function MetaButton({ id, disabled, onClick, children }) {
  return (
    <Button
      id={id}
      size="small"
      disabled={disabled}
      onClick={onClick}
      style={{
        color: disabled ? MUTED : TEXT,
        fontSize: 11,
        textTransform: "none",
        minWidth: 0,
        padding: "2px 10px",
        backgroundColor: INPUT_BG,
        border: `1px solid ${SECTION_DIVIDER}`,
        borderRadius: 4,
        lineHeight: 1.4,
      }}
    >
      {children}
    </Button>
  );
}

export function SceneEditorResetActionRow({
  disabled,
  onReset,
  label,
  buttonLabel,
  hint,
  buttonId,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: "30px",
        padding: "4px 6px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: FIELD_LABEL,
            lineHeight: 1.35,
          }}
        >
          {label}
        </div>
        {hint ? (
          <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{hint}</div>
        ) : null}
      </div>
      <MetaButton id={buttonId} disabled={disabled} onClick={onReset}>
        {buttonLabel}
      </MetaButton>
    </div>
  );
}

export function SceneEditorLanguageBar({
  selectedLanguage,
  supportedLanguages,
  createdLanguages,
  disabled,
  onLanguageChange,
  onRemoveLanguage,
}) {
  const languages = supportedLanguages || {};
  const created = Array.isArray(createdLanguages) && createdLanguages.length > 0
    ? createdLanguages
    : ["en"];
  const canRemove =
    !disabled && selectedLanguage !== "en" && created.includes(selectedLanguage);

  return (
    <MetaBar
      title={`Language - Active (${created.length})`}
      action={
        <Tooltip title="Remove selected language from this version">
          <span>
            <IconButton
              size="small"
              disabled={!canRemove}
              onClick={onRemoveLanguage}
              style={{ color: canRemove ? TEXT : MUTED, padding: 4 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <MetaValue label="Language">
        <TextField
          select
          name="selectedLanguage"
          disabled={disabled}
          value={selectedLanguage || "en"}
          onChange={(event) => onLanguageChange?.(event.target.value)}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: { backgroundColor: PANEL_BG, color: TEXT },
              },
            },
          }}
          InputProps={{
            style: {
              color: TEXT,
              fontSize: 12,
              backgroundColor: INPUT_BG,
            },
          }}
          style={{ minWidth: 160 }}
        >
          {Object.entries(languages).map(([languageKey, languageCaption]) => {
            const isActive = created.includes(languageKey);
            return (
              <MenuItem
                key={languageKey}
                value={languageKey}
                style={{
                  color: isActive ? "#9bcec0" : TEXT,
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {languageCaption}
              </MenuItem>
            );
          })}
        </TextField>
      </MetaValue>
    </MetaBar>
  );
}

export function SceneEditorPlayableSize(props) {
  const [isOpen, setOpen] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [componentSizes, setComponentSizes] = useState([]);
  const dialogClasses = useDialogStyles();

  useEffect(() => {
    if (typeof props.setTotalSize === "function") {
      props.setTotalSize(totalSize);
    }
  }, [totalSize]);

  useEffect(() => {
    async function getSizes() {
      if (_.isNil(props.selectedVersion)) return;
      const { totalSize: totalSizeResult, componentSizes: calculatedSizes } =
        await calculateVersionPlayableSize(
          props.selectedVersion,
          props.selectedBase,
          props.assetEnabledByComponent,
          props.selectedLanguage
        );
      setComponentSizes(calculatedSizes);
      setTotalSize(totalSizeResult);
      if (typeof props.setTotalSize === "function") {
        props.setTotalSize(totalSizeResult);
      }
    }
    getSizes();
  }, [props.selectedVersion, props.selectedLanguage]);

  const sizeMb = parseFloat((totalSize / 1024 / 1024).toFixed(2));

  const columns = [
    { field: "id", headerName: "Id", hide: true },
    {
      field: "name",
      headerName: "Name",
      width: 250,
      renderCell: (cell) => (
        <span style={{ width: "100%", outline: "none" }} title={cell.value}>
          {cell.value}
        </span>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 125,
      sortable: false,
    },
    {
      field: "size",
      headerName: "Size",
      width: 175,
      sortComparator: (v1, v2, param1, param2) => param1.data.size - param2.data.size,
      renderCell: (cell) => (
        <span style={{ width: "100%", outline: "none" }} title={cell.value}>
          {`${(cell.value / 1024).toFixed(2)} kb`}
        </span>
      ),
    },
  ];

  return (
    <>
      <MetaBar
        title="Playable Size"
        action={
          <MetaButton id="playableSizeDetails" onClick={() => setOpen(true)}>
            Details
          </MetaButton>
        }
      >
        <MetaValue label="Estimated">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{sizeMb} MB</span>
        </MetaValue>
      </MetaBar>

      <Dialog
        open={isOpen}
        scroll="paper"
        fullWidth
        maxWidth="sm"
        classes={{ paper: dialogClasses.paper }}
        onClose={() => setOpen(false)}
      >
        <DialogTitle disableTypography style={{ padding: "12px 16px", borderBottom: `1px solid ${SECTION_DIVIDER}` }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: MUTED,
            }}
          >
            Playable Size
          </span>
        </DialogTitle>
        <DialogContent style={{ padding: 0, backgroundColor: INPUT_BG }} className="scene-editor-size-dialog">
          <style>{`
            .scene-editor-size-dialog .MuiDataGrid-root {
              background-color: ${INPUT_BG};
              color: ${TEXT};
              border: none;
            }
            .scene-editor-size-dialog .MuiDataGrid-columnHeaders {
              background-color: ${BAR_BG};
              color: ${FIELD_LABEL};
              border-bottom: 1px solid ${SECTION_DIVIDER};
            }
            .scene-editor-size-dialog .MuiDataGrid-cell {
              color: #d6d6d6;
              border-color: ${SECTION_DIVIDER};
            }
            .scene-editor-size-dialog .MuiDataGrid-row:hover {
              background-color: rgba(144, 202, 249, 0.08);
            }
            .scene-editor-size-dialog .MuiDataGrid-columnSeparator {
              display: none;
            }
          `}</style>
          <DataGrid
            id="sizeDataGrid"
            rows={componentSizes}
            columns={columns}
            hideFooterRowCount
            hideFooterSelectedRowCount
            hideFooter
            disableSelectionOnClick
            style={{
              backgroundColor: INPUT_BG,
              color: TEXT,
              border: "none",
              fontSize: 12,
            }}
          />
        </DialogContent>
        <DialogActions
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: BAR_BG,
            borderTop: `1px solid ${SECTION_DIVIDER}`,
            padding: "12px 16px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
              Total estimated size: {sizeMb} MB
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: MUTED }}>
              Total max file size should be 5 MB
            </div>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function SceneEditorDefaultVersion(props) {
  const [error, setError] = useState("");
  const [defaultVersion, setDefaultVersion] = useState("");
  const noDefaultVersionText = "No default version";
  const [defaultVersionName, setDefaultVersionName] = useState(noDefaultVersionText);

  useEffect(() => {
    findDefaultVersion();
  }, [props.defaultVersionUpdated]);

  useEffect(() => {
    if (props.versionRename) {
      if (props.defaultVersion?.versionID == props.selectedVersion?._id) {
        setDefaultVersionName(props.versionRename);
        props.selectedVersion.title = props.versionRename;
        saveDefaultVersion();
      }
    }
  }, [props.versionRename]);

  function findDefaultVersion() {
    if (!props.selectedVersion) return Promise.resolve();
    return new Promise((resolve, reject) => {
      fetch(getServerUri("version/findDefaultVersion"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          baseID: props.selectedVersion.baseID,
          companyID: props.selectedVersion?.companyID,
        }),
      })
        .then((response) => {
          if (response.status === 200) {
            response.json().then((fetchedVersion) => {
              setDefaultVersion(fetchedVersion);
              setDefaultVersionName(fetchedVersion ? fetchedVersion.versionName : noDefaultVersionText);
              resolve(fetchedVersion);
            });
          } else {
            response
              .text()
              .then((err) => reject(err))
              .catch((err) => console.log(err));
          }
        })
        .catch((err) => reject(err));
    });
  }

  function goToDefaultVersion() {
    const searchBar = document.getElementById("searchBar");
    if (!searchBar) return;
    searchBar.focus();
    searchBar.value = defaultVersionName;
    props.setSearchBarValue(searchBar.value);
  }

  function saveDefaultVersion() {
    props.setDefaultVersionUpdated(false);
    if (defaultVersion) {
      if (_.isNil(props.selectedVersion)) return;
      fetch(getServerUri("version/updateDefaultVersion"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionName: props.selectedVersion.title,
          versionID: props.selectedVersion._id,
          baseID: props.selectedVersion.baseID,
          companyID: props.selectedVersion.companyID,
        }),
      })
        .then((response) => {
          if (response.status === 200) {
            setDefaultVersion(props.selectedVersion);
            setDefaultVersionName(props.selectedVersion.title);
            props.setDefaultVersionUpdated(true);
            props.getDefaultVersion(props.selectedVersion);
          } else if (response.status === 401) {
            response
              .text()
              .then((textResponse) => {
                console.log({ message: "error updating", textResponse });
              })
              .catch(console.error);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      fetch(getServerUri("version/insertDefaultVersion"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          versionName: props.selectedVersion.title,
          versionID: props.selectedVersion._id,
          gameID: props.selectedVersion.gameID,
          baseID: props.selectedVersion.baseID,
          companyID: props.selectedVersion.companyID,
          userID: props.selectedVersion.userID,
        }),
      })
        .then((response) => {
          if (response.status === 200) {
            response
              .json()
              .then((object) => {
                setDefaultVersionName(props.selectedVersion ? props.selectedVersion.title : noDefaultVersionText);
                setDefaultVersion(props.selectedVersion);
                props.getDefaultVersion(props.selectedVersion);
                props.setDefaultVersionUpdated(true);
                console.info("Progress Received: ", object);
              })
              .catch((err) => {
                setError(err);
              });
          } else {
            response
              .json()
              .then((object) => {
                setError("Server did not process the request");
                console.dir(object);
              })
              .catch((err) => {
                console.log("can't parse response as json", err);
              });
          }
        })
        .catch((err) => {
          setError("Could not send request to server");
          console.error(err);
        });
    }
  }

  const isCurrentDefault = defaultVersion?.versionID == props.selectedVersion?._id;
  const hasDefault = defaultVersionName !== noDefaultVersionText;
  const buttonText = isCurrentDefault ? "Default" : "Set Default";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: "30px",
        padding: "4px 6px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: FIELD_LABEL,
            lineHeight: 1.35,
          }}
        >
          Default Version
        </div>
        <div style={{ marginTop: 2, minWidth: 0 }}>
          {error ? (
            <span style={{ fontSize: 11, color: "#e57373" }}>{String(error)}</span>
          ) : (
            <Tooltip title={hasDefault ? defaultVersionName : "No default version"}>
              <span>
                <button
                  id="versionName"
                  type="button"
                  disabled={!hasDefault}
                  onClick={goToDefaultVersion}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    maxWidth: "100%",
                    fontSize: 11,
                    color: hasDefault ? "#90caf9" : MUTED,
                    cursor: hasDefault ? "pointer" : "default",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {defaultVersionName}
                </button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <Tooltip
        title={
          "If Set Default is pressed, the selected version becomes the default. " +
          "All future versions will be created based on it."
        }
      >
        <span>
          <MetaButton
            id="setDefaultVersion"
            disabled={isCurrentDefault}
            onClick={() => saveDefaultVersion()}
          >
            {buttonText}
          </MetaButton>
        </span>
      </Tooltip>
    </div>
  );
}
